import { CustomerFormResponse } from '@b2b/common/services/api/ordersApi/types';
import { ApiService } from '@b2b/common/services/api/types';
import { ACMEResponse, extractACMEPayload, TODO } from '@b2b/types/core';
import { MembershipDetails } from '@b2b/types/membership';
import angular, { IPromise } from 'angular';
import { MembershipDetailsVM } from '../../../../membership/detail/controllers/types';
import { CustomerIndividualMembership, CustomerIndividualMembershipListItem } from '../types';

export class CustomersIndividualsDetailServiceFactory {
  static $inject = ['ApiService', 'OrderService'];

  constructor(private apiService: ApiService, private orderService: TODO) {}

  /**
   * Create a service instance at the component level
   */
  createInstance() {
    return new CustomersIndividualsDetailService(this.apiService, this.orderService);
  }
}

export class CustomersIndividualsDetailService {
  constructor(private apiService: ApiService, private orderService: TODO) {}

  reset() {}

  getMembershipData(formsList: CustomerFormResponse[]) {
    const promises = new Map<number, IPromise<{ data: ACMEResponse<MembershipDetails> }>>();
    const membershipForms = formsList.filter((item) => item.formCategoryType === 'membership');

    // Dedupe membership form calls
    for (const item of membershipForms) {
      if (promises.has(item.membershipId)) {
        continue;
      }

      promises.set(item.membershipId, this.apiService.membership.detail(item.membershipId));
    }

    const membershipPromises = membershipForms
      .map((item) => promises.get(item.membershipId))
      .filter((item) => item != null);

    return Promise.all(membershipPromises).then((responses) => {
      return responses.map(extractACMEPayload()).filter((item) => !!item);
    });
  }

  getFormMetaData(formsList: CustomerFormResponse[]) {
    return this.getMembershipData(formsList).then((memberships) => {
      const membershipsData: Record<number, { membershipLevel: string; membershipOffering: string }> = {};

      for (const membership of memberships as MembershipDetailsVM[]) {
        const membershipLevel = membership.membershipCategory;
        const membershipOffering = membership.offeringName;

        membershipsData[membership.id] = {
          membershipLevel,
          membershipOffering,
        };
      }

      return membershipsData;
    });
  }

  getMembershipLifecycle(itemType: TODO) {
    return this.orderService.getItemType(itemType, true);
  }

  groupCustomerMembershipsByMembershipNumber(list: CustomerIndividualMembership[]) {
    const membershipsGrouped = list.reduce((acc, membership) => {
      const groupName = membership.acmeMembershipNumber;

      if (!acc[groupName]) {
        acc[groupName] = [];
      }

      acc[groupName].push(membership);

      return acc;
    }, {} as Record<string, TODO[]>);

    const flattenedList: CustomerIndividualMembershipListItem[] = [];

    for (const groupName in membershipsGrouped) {
      const group = membershipsGrouped[groupName];

      flattenedList.push({
        rowName: `ACME Membership Number: ${groupName}`,
        $$treeLevel: 0,
      } as CustomerIndividualMembershipListItem);

      /**
       * sort by membershipExpirationDate ascending, then by id ascending
       */
      const sortedMembershipsList: CustomerIndividualMembership[] = this.sortCustomerMemberships(group);

      flattenedList.push(...(sortedMembershipsList as CustomerIndividualMembershipListItem[]));
    }

    return flattenedList;
  }

  sortCustomerMemberships(list: CustomerIndividualMembership[]): CustomerIndividualMembership[] {
    return list.sort((a: CustomerIndividualMembership, b: CustomerIndividualMembership) => {
      if (a.membershipExpirationDate < b.membershipExpirationDate) return 1;
      if (a.membershipExpirationDate > b.membershipExpirationDate) return -1;
      if (a.membershipCategory.localeCompare(b.membershipCategory) > 0) return 1;
      if (a.membershipCategory.localeCompare(b.membershipCategory) < 0) return -1;
      if (a.offeringName.localeCompare(b.offeringName) > 0) return 1;
      if (a.offeringName.localeCompare(b.offeringName) < 0) return -1;
      return a.id - b.id;
    });
  }
}

export const CustomersIndividualsDetailServiceModule = angular
  .module('acme.view.application.customers.individuals.detail.service', [
    'acme.common.service.api',
    'acme.common.service.order',
  ])
  .service('CustomersIndividualsDetailServiceFactory', CustomersIndividualsDetailServiceFactory).name;
